import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Alert } from '@mui/material';
import { PiMagnifyingGlass, PiX } from 'react-icons/pi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import UnifiedJobQueue from './UnifiedJobQueue';
import WorkflowSetup from './WorkflowSetup';

export default function JobDashboard() {
  const [showNewJobForm, setShowNewJobForm] = useState(false);

  
  // Search and filter state with persistence
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('jobDashboard.searchTerm') || '');
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem('jobDashboard.statusFilter') || '');
  const [practiceFilter, setPracticeFilter] = useState(() => localStorage.getItem('jobDashboard.practiceFilter') || '');
  const [dateFilter, setDateFilter] = useState(() => localStorage.getItem('jobDashboard.dateFilter') || '');
  
  // Persist filter state
  const updateSearchTerm = (value: string) => {
    setSearchTerm(value);
    localStorage.setItem('jobDashboard.searchTerm', value);
  };
  
  const updateStatusFilter = (value: string) => {
    setStatusFilter(value);
    localStorage.setItem('jobDashboard.statusFilter', value);
  };
  
  const updatePracticeFilter = (value: string) => {
    setPracticeFilter(value);
    localStorage.setItem('jobDashboard.practiceFilter', value);
  };
  
  const updateDateFilter = (value: string) => {
    setDateFilter(value);
    localStorage.setItem('jobDashboard.dateFilter', value);
  };
  const queryClient = useQueryClient();

  // Get all jobs with enhanced error handling
  const { data: jobs = [], error: queryError, isError } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => apiService.getAllJobs(),
    refetchInterval: (query) => {
      // Don't poll on errors
      if (query.state.error) return false;
      // Poll faster if there are running jobs
      const jobs = query.state.data as any[] || [];
      const hasRunningJobs = jobs.some((job: any) => 
        ['RUNNING', 'PENDING', 'WAITING_FOR_INPUT'].includes(job.status)
      );
      return hasRunningJobs ? 5000 : 30000; // 5s if running, 30s otherwise
    },
    staleTime: 1000,
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('Authentication required')) {
        console.warn('Jobs query authentication error');
        return false;
      }
      return failureCount < 2;
    }
  });

    // Handle authentication errors
  React.useEffect(() => {
    if (isError && queryError) {
      console.error('Jobs query error:', queryError);
      // Don't show error for auth failures - user will be redirected
      if (!queryError?.message?.includes('Authentication required')) {
        console.error('Failed to load jobs:', queryError.message);
      }
    }
  }, [isError, queryError]);



  // Job control mutations with optimistic updates
  const pauseJobMutation = useMutation({
    mutationFn: (jobId: string) => apiService.pauseJob(jobId),
    onMutate: async (jobId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['jobs'] });
      
      // Snapshot the previous value
      const previousJobs = queryClient.getQueryData(['jobs']);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['jobs'], (old: any[]) =>
        old?.map(job => 
          job.jobId === jobId 
            ? { ...job, status: 'PAUSED' }
            : job
        )
      );
      
      return { previousJobs };
    },
    onError: (err, jobId, context) => {
      // Rollback on error
      queryClient.setQueryData(['jobs'], context?.previousJobs);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  const resumeJobMutation = useMutation({
    mutationFn: (jobId: string) => apiService.resumeJob(jobId),
    onMutate: async (jobId) => {
      await queryClient.cancelQueries({ queryKey: ['jobs'] });
      const previousJobs = queryClient.getQueryData(['jobs']);
      
      queryClient.setQueryData(['jobs'], (old: any[]) =>
        old?.map(job => 
          job.jobId === jobId 
            ? { ...job, status: 'RUNNING' }
            : job
        )
      );
      
      return { previousJobs };
    },
    onError: (err, jobId, context) => {
      queryClient.setQueryData(['jobs'], context?.previousJobs);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => apiService.cancelJob(jobId),
    onMutate: async (jobId) => {
      await queryClient.cancelQueries({ queryKey: ['jobs'] });
      const previousJobs = queryClient.getQueryData(['jobs']);
      
      queryClient.setQueryData(['jobs'], (old: any[]) =>
        old?.map(job => 
          job.jobId === jobId 
            ? { ...job, status: 'CANCELLED' }
            : job
        )
      );
      
      return { previousJobs };
    },
    onError: (err, jobId, context) => {
      queryClient.setQueryData(['jobs'], context?.previousJobs);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  const renameJobMutation = useMutation({
    mutationFn: ({ jobId, newName }: { jobId: string; newName: string }) => 
      apiService.renameJob(jobId, newName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  const deleteJobMutation = useMutation({
    mutationFn: (jobId: string) => apiService.deleteJob(jobId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  const handleNewJob = (executionArn: string) => {
    setShowNewJobForm(false);
    // Force immediate refresh
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.refetchQueries({ queryKey: ['jobs'] });
  };

  // Filter and search jobs
  const filteredJobs = useMemo(() => {
    const jobsArray = Array.isArray(jobs) ? jobs : [];
    return jobsArray.filter((job: any) => {
      const matchesSearch = !searchTerm || 
        (job.displayName || job.number || job.input?.number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (job.practice || job.input?.practice || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (job.workflows || job.input?.workflows || []).some((w: string) => w.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = !statusFilter || job.status === statusFilter;
      const matchesPractice = !practiceFilter || (job.practice || job.input?.practice) === practiceFilter;
      
      let matchesDate = true;
      if (dateFilter) {
        const jobDate = new Date(job.createdAt);
        const today = new Date();
        switch (dateFilter) {
          case 'today':
            matchesDate = jobDate.toDateString() === today.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = jobDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = jobDate >= monthAgo;
            break;
        }
      }
      
      return matchesSearch && matchesStatus && matchesPractice && matchesDate;
    });
  }, [jobs, searchTerm, statusFilter, practiceFilter, dateFilter]);
  
  // Get unique practices for filter dropdown
  const uniquePractices = useMemo(() => {
    const practices = new Set();
    const jobsArray = Array.isArray(jobs) ? jobs : [];
    jobsArray.forEach((job: any) => {
      const practice = job.practice || job.input?.practice;
      if (practice) practices.add(practice);
    });
    return Array.from(practices);
  }, [jobs]);
  
  const clearFilters = () => {
    updateSearchTerm('');
    updateStatusFilter('');
    updatePracticeFilter('');
    updateDateFilter('');
  };
  
  const hasActiveFilters = searchTerm || statusFilter || practiceFilter || dateFilter;

  // Handle authentication errors after all hooks
  if (isError && queryError?.message?.includes('Authentication required')) {
    // Don't render anything, user will be redirected
    return null;
  }

  return (
    <Box>
      {/* Error Alert */}
      {isError && !queryError?.message?.includes('Authentication required') && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load jobs: {queryError?.message || 'Unknown error'}
          <Button onClick={() => window.location.reload()} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}


      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => updateSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <PiMagnifyingGlass style={{ marginRight: 8, color: 'inherit', opacity: 0.6 }} />
            }}
            sx={{ minWidth: 200 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => updateStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="RUNNING">Running</MenuItem>
              <MenuItem value="SUCCEEDED">Completed</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
              <MenuItem value="QUEUED">Queued</MenuItem>
              <MenuItem value="WAITING_FOR_INPUT">Waiting</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Practice</InputLabel>
            <Select
              value={practiceFilter}
              onChange={(e) => updatePracticeFilter(e.target.value)}
              label="Practice"
            >
              <MenuItem value="">All</MenuItem>
              {uniquePractices.map((practice: any) => (
                <MenuItem key={practice} value={practice}>
                  {practice.charAt(0).toUpperCase() + practice.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date</InputLabel>
            <Select
              value={dateFilter}
              onChange={(e) => updateDateFilter(e.target.value)}
              label="Date"
            >
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Past Week</MenuItem>
              <MenuItem value="month">Past Month</MenuItem>
            </Select>
          </FormControl>
          
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<PiX />}
              onClick={clearFilters}
              variant="outlined"
            >
              Clear
            </Button>
          )}
        </Box>
        
        {hasActiveFilters && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {searchTerm && <Chip label={`Search: ${searchTerm}`} size="small" onDelete={() => updateSearchTerm('')} />}
            {statusFilter && <Chip label={`Status: ${statusFilter}`} size="small" onDelete={() => updateStatusFilter('')} />}
            {practiceFilter && <Chip label={`Practice: ${practiceFilter}`} size="small" onDelete={() => updatePracticeFilter('')} />}
            {dateFilter && <Chip label={`Date: ${dateFilter}`} size="small" onDelete={() => updateDateFilter('')} />}
          </Box>
        )}
      </Paper>

      {/* Jobs List */}
      
      <UnifiedJobQueue
        jobs={filteredJobs}
        variant="compact"
        features={{
          showPauseResume: true,
          showCancel: true,
          groupByStatus: true,
          maxCompletedGroups: 10,
        }}
        onPauseJob={(jobId) => pauseJobMutation.mutate(jobId)}
        onResumeJob={(jobId) => resumeJobMutation.mutate(jobId)}
        onCancelJob={(jobId) => cancelJobMutation.mutate(jobId)}
        onRenameJob={(jobId, newName) => renameJobMutation.mutate({ jobId, newName })}
        onDeleteJob={(jobId) => deleteJobMutation.mutate(jobId)}
      />

      {/* New Job Form */}
      {showNewJobForm && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Submit New Job
          </Typography>
          <WorkflowSetup onExecute={handleNewJob} />
        </Paper>
      )}
    </Box>
  );
}